import { useMemo, useState, useRef } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import BarChart from "../../../components/graphs/BarChart";
import { useTranslation } from "react-i18next";
import BarGraphLoader from "../../../components/skeletonLoaders/BarGraphLoader";
import useAuthValue from "../../authModule/hooks/useAuthValue";
//@ts-ignore
import { useScreenshot } from "use-react-screenshot";
import useSelectedHospitalValue from "../../hospitalModule/hooks/useSelectedHospitalValue";
import { TABLE_HEADER_ARRAY } from "../../../utils/Types";
import ChartContainer, {
  CHART_TYPE,
  RenderChartProps,
} from "../../../components/ChartContainer";
import useDashboardExport from "../hooks/useDashboardExport";
import GroupBarChart from "../../../components/graphs/GroupBarChart";
import useGetSurgicalVsTotalCaseTimeQuery from "../hooks/useGetSurgicalVsTotalCaseTime";
import useGetReadyToIncisionLagQuery from "../hooks/useGetReadyToIncisionLag";
import useGetOnTimeStartEndQuery from "../hooks/useGetSurgeonOnTimeStartEndQuery";
import { ON_TIME_STARTS_ENDS_FOR_SURGEONS_RESPONSE } from "../types/ResponseTypes";
import { buildQueryStringFromFilters } from "../../../helpers/buildQueryStringFromFilters";
import useFilterValue from "../hooks/useFilterValue";
import TableChart from "../../../components/graphs/TableChart";

interface MasterDataTableProps {
  exportRoleBaesdScreenshot?: any;
}

// Table row type for On-Time Start/End Percentage
export type OTOnTimeStartEndRow = {
  surgeonName: string | undefined;
  totalCases: number | undefined;
  percentOnTimeStarts: string | undefined;
  percentOnTimeEnds: string | undefined;
};

// Table headers for DynamicTable
const otOnTimeStartEndHeaders: TABLE_HEADER_ARRAY = [
  { key: "surgeonName", label: "Surgeon Name" },
  { key: "totalCases", label: "Total Cases", position: "center" },
  { key: "percentOnTimeStarts", label: "% On-Time Starts", position: "center" },
  { key: "percentOnTimeEnds", label: "% On-Time Ends", position: "center" },
];

// Helper to map API response to table rows
function mapOnTimeStartEndRows(
  data: Array<ON_TIME_STARTS_ENDS_FOR_SURGEONS_RESPONSE>
): OTOnTimeStartEndRow[] {
  return (data || []).map((row) => ({
    surgeonName: row.surgeon,
    totalCases: row.totalCases,
    percentOnTimeStarts: `${row.onTimeStartsPercentage?.toFixed(0)}%`,
    percentOnTimeEnds: `${row.onTimeEndsPercentage?.toFixed(0)}%`,
  }));
}

// Extract data fetching hooks into a custom hook
const useSurgeonData = (
  tenantId: string | undefined,
  hospitalId: string | undefined,
  queryString: string | undefined
) => {
  const {
    data: { data: surgicalVsTotalCaseTimeData } = {},
    isLoading: isSurgicalVsTotalCaseTime,
  } = useGetSurgicalVsTotalCaseTimeQuery(
    tenantId ?? "",
    hospitalId ?? "",
    queryString
  );

  const {
    data: { data: readyToIncisionLagData } = {},
    isLoading: isReadyToIncisionLag,
  } = useGetReadyToIncisionLagQuery(
    tenantId ?? "",
    hospitalId ?? "",
    queryString
  );

  const {
    data: { data: onTimeStartEndPercentageData } = {},
    isLoading: isOnTimeStartEndPercentage,
  } = useGetOnTimeStartEndQuery(tenantId ?? "", hospitalId ?? "", queryString);

  return {
    surgicalVsTotalCaseTimeData,
    isSurgicalVsTotalCaseTime,
    readyToIncisionLagData,
    isReadyToIncisionLag,
    onTimeStartEndPercentageData,
    isOnTimeStartEndPercentage,
  };
};

const SurgeonAnalysis: React.FC<MasterDataTableProps> = ({
  exportRoleBaesdScreenshot,
}) => {
  const { t } = useTranslation();
  const currentHospital = useSelectedHospitalValue();
  const { tenantId: authTenantId, hospitalId: authHospitalId } = useAuthValue();
  const tenantId = currentHospital?.tenantId ?? authTenantId;
  const hospitalId = currentHospital?.uuid ?? authHospitalId;
  const screenshotRef = useRef(null);
  const [isExporting, setIsExporting] = useState(false);
  const [, takeScreenshot] = useScreenshot();
  const filters = useFilterValue();
  const queryString = buildQueryStringFromFilters(filters);

  const {
    surgicalVsTotalCaseTimeData,
    isSurgicalVsTotalCaseTime,
    readyToIncisionLagData,
    isReadyToIncisionLag,
    onTimeStartEndPercentageData,
    isOnTimeStartEndPercentage,
  } = useSurgeonData(tenantId, hospitalId, queryString);

  const otOnTimeStartEndRows = useMemo(
    () => mapOnTimeStartEndRows(onTimeStartEndPercentageData || []),
    [onTimeStartEndPercentageData]
  );

  // Transform otUtilizationData for BarChart
  const readyToIncisionLagChartData = useMemo(() => {
    if (!readyToIncisionLagData || !Array.isArray(readyToIncisionLagData))
      return [];
    return readyToIncisionLagData.map((item) => ({
      label: item.surgeon,
      value: item.averageLagMinutes,
    }));
  }, [readyToIncisionLagData]);

  const surgicalVsTotalCaseTimeChartData = useMemo(() => {
    if (
      !surgicalVsTotalCaseTimeData ||
      !Array.isArray(surgicalVsTotalCaseTimeData)
    )
      return [];
    return surgicalVsTotalCaseTimeData.map((item: any) => ({
      label: item.surgeon,
      surgicalTime: item.surgicalTimeAvg,
      totalCaseTime: item.totalCaseTimeAvg,
      avgPercentage: item.surgicalTimePercentage,
    }));
  }, [surgicalVsTotalCaseTimeData]);

  useDashboardExport({
    exportRef: exportRoleBaesdScreenshot,
    screenshotRef,
    setIsExporting,
    takeScreenshot,
    filename: "orva-surgeon-analysis.pdf",
  });

  const renderChart = ({
    data,
    title,
    tooltipTitle,
    isLoading,
    props,
    chartType,
  }: RenderChartProps) => {
    if (isLoading && chartType !== CHART_TYPE.TABLE) {
      return <BarGraphLoader {...props} />;
    }
    switch (chartType) {
      case CHART_TYPE.GROUP_BAR:
        return (
          <GroupBarChart
            key={hospitalId}
            data={data}
            series={series}
            title={t(title)}
            tooltipTitle={t(tooltipTitle)}
            {...props}
          />
        );
      case CHART_TYPE.TABLE:
        return (
          <TableChart
            title={t(title)}
            tooltipTitle={t(tooltipTitle)}
            headers={Array.isArray(data) ? [] : data?.headers}
            rowData={Array.isArray(data) ? [] : data?.rowData}
            isLoading={isOnTimeStartEndPercentage}
            {...props}
          />
        );
      default:
        return (
          <BarChart
            key={hospitalId}
            title={t(title)}
            tooltipTitle={t(tooltipTitle)}
            data={data}
            {...props}
          />
        );
    }
  };

  const series = [
    { key: "surgicalTime", color: "#5921F2", label: "Surgical Time" },
    { key: "totalCaseTime", color: "#B07CF6", label: "Total Case Time" },
  ];

  return (
    <Box ref={screenshotRef} flex={1} py={2} sx={{ px: isExporting ? 2 : 0 }}>
      <Grid container spacing={1.5}>
        <Grid size={{ xs: 12, md: 12 }}>
          <ChartContainer
            isLoading={isSurgicalVsTotalCaseTime}
            data={surgicalVsTotalCaseTimeChartData ?? []}
            title="Surgical_Time_vs_Total_Case_Time"
            tooltipTitle="Surgical_Time_vs_Total_Case_Time_Tooltip"
            chartType={CHART_TYPE.GROUP_BAR}
            props={{
              showLabels: false,
              showXAxis: true,
              categoryFontSize: 12,
              labelColNumber: 2,
              showAverageLine: true,
            }}
            renderChart={renderChart}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <ChartContainer
            isLoading={isReadyToIncisionLag}
            data={readyToIncisionLagChartData ?? []}
            title="Ready_to_Incision_Lag_Avg"
            tooltipTitle="Ready_to_Incision_Lag_Avg_Tooltip"
            props={{
              showLabels: false,
              showXAxis: true,
              categoryFontSize: 12,
              labelColNumber: 2,
              showAverageLine: true,
              showLabelInHeader: false,
            }}
            renderChart={renderChart}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <ChartContainer
            isLoading={isOnTimeStartEndPercentage}
            data={{
              headers: otOnTimeStartEndHeaders,
              rowData: otOnTimeStartEndRows,
            }}
            title="On_Time_Starts_Ends"
            tooltipTitle="On_Time_Starts_Ends_Tooltip"
            chartType={CHART_TYPE.TABLE}
            props={{
              showLabels: false,
              showXAxis: true,
              categoryFontSize: 12,
              labelColNumber: 2,
              showAverageLine: true,
              showLabelInHeader: false,
            }}
            renderChart={renderChart}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default SurgeonAnalysis;
